import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Renderer2,
  computed,
  inject,
  input,
  signal,
  viewChild
} from '@angular/core';
import {CompositionGroupingPreferenceService} from '../composition-grouping-preference.service';

/** Keeps generated element ids apart across several instances on one page (or several Storybook stories). */
let settingsCounter = 0;

/**
 * "Anzeigeoptionen" — the Feature #534 display-settings control shared by the event-details "Artikel" card and
 * the single-event-analysis "Verkäufe pro Kategorie" table header.
 *
 * ## Why a gear and a panel, not a bare switch
 *
 * The first cut of #534 put a naked "Kombinationen einzeln" switch straight into both headers. A switch on its
 * own states a *setting name* and nothing else: it cannot say what the setting does, that a Sonderpreis is
 * deliberately NOT split out, or that the choice is remembered — and a header has no room for that sentence
 * next to a switch. The user asked for a settings gear instead, on both pages. So the switch itself is
 * unchanged (same style-guide markup, same shared preference); it just moved into a small panel that has room
 * for the explanation, and the header now carries a quiet 16px gear.
 *
 * ## Why not `cqme-dropdown`
 *
 * `cqme-dropdown` wraps `@tailwindplus/elements`' `<el-menu popover>`. That is **menu** semantics: a screen
 * reader announces `menu`/`menuitem`, and a menu item is a command you invoke, not a state you set. A switch
 * inside a menu is announced wrongly and behaves wrongly under menu keyboard conventions. This panel is
 * therefore a plain, component-managed `role="dialog"` — the only other option would have been a modal, which
 * is far too heavy for one boolean.
 *
 * ## Behaviour contract (pinned by the spec)
 *
 * - Closed by default; the trigger opens it and moves focus to the switch.
 * - `Escape` closes it and returns focus to the trigger.
 * - A click **outside** the component closes it; a click **inside** (panel or trigger) never does.
 * - Focus leaving the component closes it (`focusout` with a `relatedTarget` outside the host).
 * - Flipping the switch writes the shared preference **immediately** — there is no save button and no local
 *   copy of the state to get out of sync.
 * - The trigger stops click propagation: on the event-details page the summary card's header advances to the
 *   next card on click, and opening the settings must not also flip the card.
 *
 * The document click listener is registered **only while the panel is open** (and torn down through
 * `DestroyRef`), so a closed control costs nothing app-wide.
 */
@Component({
  selector: 'app-composition-grouping-settings',
  standalone: true,
  templateUrl: './composition-grouping-settings.component.html',
  styleUrl: './composition-grouping-settings.component.scss'
})
export class CompositionGroupingSettingsComponent {
  protected readonly preference = inject(CompositionGroupingPreferenceService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly changeDetector = inject(ChangeDetectorRef);

  /**
   * Id stem for the trigger/panel/switch trio. Defaults to a generated, page-unique value, so callers only set
   * it when a test or a story wants a stable, addressable id.
   */
  readonly id = input<string>(`composition-grouping-settings-${++settingsCounter}`);

  /** `true` while the panel is open. The trigger's `aria-expanded` mirrors it. */
  protected readonly open = signal(false);

  /**
   * `true` when the panel must anchor to the gear's LEFT edge instead of its right one. A right-anchored
   * `w-72` panel needs ~18rem of room to the left of the gear; when the gear sits near the left edge of the
   * viewport (a header that wrapped on a phone), that room is not there and the panel would run off-screen.
   * Measured in {@link openPanel} from the trigger's live rect, so it follows whatever layout the host put
   * the gear in.
   */
  protected readonly alignLeft = signal(false);

  /** `w-72` in px plus the page's phone gutter — the room a right-anchored panel needs to the gear's left. */
  private static readonly PANEL_WIDTH_PX = 288;
  private static readonly VIEWPORT_GUTTER_PX = 16;

  /** `aria-controls` on the trigger and `id` on the panel — they must be the same string. */
  protected readonly panelId = computed(() => `${this.id()}-panel`);
  /** `aria-labelledby` on the panel points at the title carrying this id. */
  protected readonly titleId = computed(() => `${this.id()}-title`);
  /** `<label for>` ↔ the switch's `id`. */
  protected readonly switchId = computed(() => `${this.id()}-switch`);

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly groupingSwitch = viewChild<ElementRef<HTMLInputElement>>('groupingSwitch');

  /** Removes the while-open document click listener; `undefined` while the panel is closed. */
  private stopListeningForOutsideClicks?: () => void;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stopListeningForOutsideClicks?.());
  }

  /**
   * Opens/closes the panel from the gear. The `stopPropagation` is load-bearing, not defensive: the
   * event-details summary card advances to the next card when its header is clicked.
   */
  protected onTriggerClick(event: MouseEvent): void {
    event.stopPropagation();
    if (this.open()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  /** A click anywhere inside the panel stays inside — same reason as {@link onTriggerClick}. */
  protected onPanelClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  /** `Escape` closes and hands focus back to the gear, so keyboard users do not land at the top of the page. */
  @HostListener('keydown.escape', ['$event'])
  protected onEscape(event: KeyboardEvent): void {
    if (!this.open()) {
      return;
    }
    event.stopPropagation();
    this.closePanel();
    this.trigger().nativeElement.focus();
  }

  /**
   * Closes when focus leaves the component entirely (tabbing past the switch, or a click that focuses something
   * else). `relatedTarget` is the element focus moved TO; `null` (a click on non-focusable chrome, or the window
   * losing focus) counts as "outside".
   */
  @HostListener('focusout', ['$event'])
  protected onFocusOut(event: FocusEvent): void {
    if (!this.open()) {
      return;
    }
    const next = event.relatedTarget;
    // Close ONLY when focus demonstrably moved to something outside this component. A `null` relatedTarget
    // is not that: it is what a mousedown on non-focusable content produces (the label text of the switch,
    // the explanation paragraph — the focused switch blurs to `body` first, the click lands afterwards), and
    // closing on it made the switch itself unclickable. Outside clicks are the document listener's job,
    // Escape is the keydown handler's; this hook only has to catch a real tab-out.
    if (!(next instanceof Node) || this.host.nativeElement.contains(next)) {
      return;
    }
    this.closePanel();
  }

  private openPanel(): void {
    // Decide the anchor side BEFORE the panel renders, from where the gear actually is right now.
    const triggerRect = this.trigger().nativeElement.getBoundingClientRect();
    const roomToTheLeft = triggerRect.right - CompositionGroupingSettingsComponent.VIEWPORT_GUTTER_PX;
    this.alignLeft.set(roomToTheLeft < CompositionGroupingSettingsComponent.PANEL_WIDTH_PX);

    this.open.set(true);
    // Render the panel NOW so the switch exists to receive focus in the same tick — an `effect`/`setTimeout`
    // would push the focus move into a later task, which is both harder to test and visibly laggy.
    this.changeDetector.detectChanges();
    this.groupingSwitch()?.nativeElement.focus();

    this.stopListeningForOutsideClicks?.();
    this.stopListeningForOutsideClicks = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      // Belt and braces: the trigger and the panel both stop propagation, so this normally only ever sees a
      // genuinely outside click — but a click on the component's own wrapper must not close it either.
      const target = event.target;
      if (target instanceof Node && this.host.nativeElement.contains(target)) {
        return;
      }
      this.closePanel();
    });
  }

  private closePanel(): void {
    this.open.set(false);
    this.stopListeningForOutsideClicks?.();
    this.stopListeningForOutsideClicks = undefined;
  }
}
